import { useEffect, useState, type HTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

type TooltipProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof variants> & {
    text?: string
    subText?: string
    node?: ReactNode
    theme?: 'default' | 'primary'
    delay?: boolean
    position?: 'up' | 'down' | 'left' | 'right'
    width?: number
    wrap?: boolean
    open?: boolean
    close?: boolean
    disabled?: boolean
    help?: boolean
    tooltipId?: string
  }

const variants = cva(
  'absolute left-1/2 text-14 h-auto px-2.5 -translate-x-1/2 z-[9999] flex w-fit flex-col justify-center rounded-xl text-white opacity-0 transition-all scale-50 duration-300 pointer-events-none ease-in-out shadow-lg',
  {
    variants: {
      theme: {
        default: 'text-white bg-dark-950',
        primary: 'text-white bg-primary-500',
      },
      position: {
        up: 'top-0 translate-y-[calc(-100%-8px)]',
        down: 'top-full translate-y-2',
        left: 'top-1/2 -translate-y-1/2 left-0 translate-x-[calc(-100%-8px)]',
        right: 'top-1/2 -translate-y-1/2 right-0 translate-x-2',
      },
      wrap: {
        true: 'max-w-full',
        false: '',
      },
      condensed: {
        true: 'text-12',
        false: '',
      },
      align: {
        center: 'text-center items-center',
        left: 'text-left items-start',
        right: 'text-right items-end',
      },
    },
    defaultVariants: {
      theme: 'default',
      position: 'up',
      wrap: false,
      condensed: false,
      align: 'center',
    },
  },
)

export default function Tooltip({
  text,
  subText,
  node,
  children,
  delay,
  position,
  theme,
  width,
  wrap = false,
  condensed = false,
  align = 'center',
  open,
  close,
  disabled = false,
  help = false,
  className,
  tooltipId,
}: TooltipProps) {
  const [isDelayActive, setIsDelayActive] = useState(false)

  useEffect(() => {
    if (close) {
      setIsDelayActive(true)
    } else if (isDelayActive) {
      const timer = setTimeout(() => {
        setIsDelayActive(false)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [close, isDelayActive])

  return (
    <div
      className={cn(
        'relative cursor-default leading-none focus-within:[&>span:last-child]:scale-100 focus-within:[&>span:last-child]:opacity-100 [&>span]:hover:scale-100 [&>span]:hover:opacity-100',
        delay && `[&>span]:hover:delay-500`,
        className,
      )}
    >
      {help ? (
        <div className="dotted-underline dotted-loose cursor-help truncate text-left font-medium leading-tight">
          {children}
        </div>
      ) : (
        children
      )}
      <span
        id={tooltipId}
        role="tooltip"
        className={cn(
          variants({ theme, position, wrap, condensed, align }),
          wrap || width ? 'whitespace-normal py-1.5' : 'whitespace-nowrap py-1',
          open && '!scale-100 !opacity-100',
          (close || isDelayActive) && '!scale-0 !opacity-0',
          disabled && 'hidden',
        )}
        style={{ width: width ? `${width}px` : wrap ? 'max-content' : 'auto' }}
      >
        {node ?? text}
        {subText && <span className="text-white/70">{subText}</span>}
      </span>
    </div>
  )
}
